import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { NotificationManager, prepareRequest, useApplicationState } from '@atlas/core';
import { Button, DefaultPopup, HeaderTitle, Input, Label } from '@atlas/ui';

import { openWithdrawWindow } from 'common/redirect';
import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

const errorMaxLength = ruleOption => ({
  message: 'max_length',
  params: { ruleOption },
});

const validateFinNumber = value => {
  if (!value) return { message: 'required' };
  if (!/^\d{3}$/.test(value)) return errorMaxLength(3);
  return null;
};

const validateTransitNumber = value => {
  if (!value) return { message: 'required' };
  if (!/^\d{5}$/.test(value)) return errorMaxLength(5);
  return null;
};

const validateAccountNumber = value => {
  if (!value) return { message: 'required' };
  if (!/^\d{7,12}$/.test(value))
    return {
      message: 'max_length',
      params: { max: 12, min: 7 },
    };
  return null;
};

export const WithdrawalInteracPopup = ({ data, id }) => {
  const {
    wallet: { currency },
  } = useApplicationState();

  const { amount, formAction, paymentMethod, pmId } = data;
  const [activeInputs, setActiveInputs] = useState({
    accountNumber: false,
    finNumber: false,
    transitNumber: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finNumber, setFinNumber] = useState('');
  const [transitNumber, setTransitNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [errors, setErrors] = useState({});

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const handleInputFocus = useCallback(fieldName => {
    setActiveInputs(prev => ({ ...prev, [fieldName]: true }));
  }, []);

  const handleInputBlur = useCallback(fieldName => {
    setActiveInputs(prev => ({ ...prev, [fieldName]: false }));
  }, []);

  const handleFinNumberChange = event => {
    const value = event.target.value.replace(/\D/g, '');
    setFinNumber(value);
    const error = validateFinNumber(value);
    setErrors(prev => ({ ...prev, finNumber: error }));
  };

  const handleTransitNumberChange = event => {
    const value = event.target.value.replace(/\D/g, '');
    setTransitNumber(value);
    const error = validateTransitNumber(value);
    setErrors(prev => ({ ...prev, transitNumber: error }));
  };

  const handleAccountNumberChange = event => {
    const value = event.target.value.replace(/\D/g, '');
    setAccountNumber(value);
    const error = validateAccountNumber(value);
    setErrors(prev => ({ ...prev, accountNumber: error }));
  };

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    const finError = validateFinNumber(finNumber);
    const transitError = validateTransitNumber(transitNumber);
    const accountError = validateAccountNumber(accountNumber);

    const newErrors = {
      accountNumber: accountError,
      finNumber: finError,
      transitNumber: transitError,
    };

    setErrors(newErrors);

    if (finError || transitError || accountError) {
      return;
    }

    const request = prepareRequest({
      method: 'POST',
      url: formAction,
    });

    const requestData = {
      additionalParameters: {
        account_number: Number.parseInt(accountNumber),
        fin_number: Number.parseInt(finNumber),
        transit_number: Number.parseInt(transitNumber),
      },
      amount,
      currency,
      pmId,
    };

    setIsSubmitting(true);

    try {
      const { displayOption, redirectUrl } = await request(requestData);

      if (displayOption === 'NO_IFRAME') {
        window.location.href = redirectUrl;
        return;
      }

      openWithdrawWindow(redirectUrl);
    } catch (error) {
      NotificationManager.display({
        data: {
          headerTitle: 'withdrawal_by_interac',
          title: 'failed_sad_emoticon',
        },
        type: 'error',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    finNumber,
    transitNumber,
    accountNumber,
    formAction,
    amount,
    currency,
    pmId,
    handleClose,
  ]);

  const isFormValid =
    finNumber &&
    transitNumber &&
    accountNumber &&
    !errors.finNumber &&
    !errors.transitNumber &&
    !errors.accountNumber;

  return (
    <DefaultPopup
      className="withdrawal-interac"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big withdrawal-interac__title">
        <PaymentMethodItem amount={amount} paymentMethod={paymentMethod} showAmount />
      </HeaderTitle>

      <div className="withdrawal-interac__content">
        <div className="withdrawal-interac__content-form">
          <div className="withdrawal-interac__content-form-item">
            <Input
              active={activeInputs.finNumber}
              error={errors.finNumber}
              label="Financial Institution Number"
              onBlur={() => handleInputBlur('finNumber')}
              onChange={handleFinNumberChange}
              onFocus={() => handleInputFocus('finNumber')}
              value={finNumber}
            />
            {errors.finNumber && (
              <div className="help-text-container">
                <Label message={errors.finNumber.message} params={errors.finNumber.params} />
              </div>
            )}
          </div>

          <Input
            active={activeInputs.transitNumber}
            error={errors.transitNumber}
            label="Transit Number"
            onBlur={() => handleInputBlur('transitNumber')}
            onChange={handleTransitNumberChange}
            onFocus={() => handleInputFocus('transitNumber')}
            value={transitNumber}
          />
          {errors.transitNumber && (
            <div className="help-text-container">
              <Label message={errors.transitNumber.message} params={errors.transitNumber.params} />
            </div>
          )}

          <Input
            active={activeInputs.accountNumber}
            error={errors.accountNumber}
            label="Account Number"
            onBlur={() => handleInputBlur('accountNumber')}
            onChange={handleAccountNumberChange}
            onFocus={() => handleInputFocus('accountNumber')}
            value={accountNumber}
          />
          {errors.accountNumber && (
            <div className="help-text-container">
              <Label message={errors.accountNumber.message} params={errors.accountNumber.params} />
            </div>
          )}
        </div>

        <Button
          className="withdrawal-interac__button"
          disabled={!isFormValid || isSubmitting}
          label="withdrawal"
          loading={isSubmitting}
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalInteracPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    formAction: PropTypes.string.isRequired,
    paymentMethod: PropTypes.string.isRequired,
    pmId: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
};
